/**
 * "Suggest another time" on decline (listener) and the talker's one-tap
 * "Book this time" links. Server: server/api/decline-suggestions.js.
 */
import { createSlug } from './urlHelpers';
import { saveDeclineSuggestions } from './api';

export const DECLINE_SUGGESTIONS_MAX = 3;
export const DECLINE_SUGGESTIONS_META_KEY = 'declineSuggestions';
export const SUGGEST_START_QUERY = 'suggestStart';
const SESSION_MS = 15 * 60 * 1000;

const asMs = value => {
  if (value instanceof Date) {
    return value.getTime();
  }
  if (typeof value === 'number') {
    return value;
  }
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : NaN;
};

const txIdOf = tx => {
  if (!tx) {
    return '';
  }
  if (typeof tx === 'string') {
    return tx;
  }
  return tx?.id?.uuid || (typeof tx?.id === 'string' ? tx.id : '') || tx?.uuid || '';
};

/**
 * Upcoming suggested times saved on a declined transaction (talker side).
 *
 * @param {Object} tx
 * @param {Date} [now]
 * @returns {Array<{ start: string, end: string, label?: string }>}
 */
export const suggestionsFromTransaction = (tx, now = new Date()) => {
  const meta = tx?.attributes?.metadata?.[DECLINE_SUGGESTIONS_META_KEY];
  return upcomingSuggestions(meta?.slots, now);
};

/**
 * @param {Array} list
 * @param {Date} [now]
 */
export const upcomingSuggestions = (list, now = new Date()) => {
  const nowMs = asMs(now);
  return (Array.isArray(list) ? list : [])
    .filter(s => Number.isFinite(asMs(s?.start)) && asMs(s.start) > nowMs)
    .slice(0, DECLINE_SUGGESTIONS_MAX);
};

/**
 * In-app path for one-tap "Book this time": the listener's booking page
 * with the slot preselected; it continues straight to checkout.
 *
 * @param {Object} params
 * @param {string} params.listingId
 * @param {string} [params.name] listener display name (slug only)
 * @param {string|number} [params.start]
 * @param {string} [params.sessionFormat]
 * @returns {string}
 */
export const bookSuggestionPath = ({ listingId, name, start, sessionFormat } = {}) => {
  if (!listingId) {
    return '/s';
  }
  const params = new URLSearchParams();
  params.set('mode', 'later');
  const ms = asMs(start);
  if (Number.isFinite(ms)) {
    params.set(SUGGEST_START_QUERY, String(ms));
  }
  if (sessionFormat) {
    params.set('format', sessionFormat);
  }
  params.set('from', 'decline');
  return `/l/${createSlug(name || 'listener')}/${listingId}/book?${params.toString()}`;
};

/**
 * Book-page values for a suggested start (15-minute session).
 *
 * @param {Object} params
 * @param {string|number} params.suggestStart ms timestamp
 * @param {string} [params.format]
 * @param {Array<string>} [params.offeredFormats]
 * @param {Function} params.variantNameForFormat
 * @param {Date} [params.now]
 * @returns {Object|null}
 */
export const suggestedBookingValues = ({
  suggestStart,
  format,
  offeredFormats = [],
  variantNameForFormat,
  now = new Date(),
} = {}) => {
  const startMs = Number(suggestStart);
  if (!Number.isFinite(startMs) || startMs <= asMs(now)) {
    return null;
  }
  const sessionFormat =
    format && (offeredFormats.length === 0 || offeredFormats.includes(format))
      ? format
      : offeredFormats[0] || null;
  if (!sessionFormat) {
    return null;
  }
  return {
    sessionFormat,
    priceVariantName:
      typeof variantNameForFormat === 'function' ? variantNameForFormat(sessionFormat) : undefined,
    bookingStartTime: String(startMs),
    bookingEndTime: String(startMs + SESSION_MS),
  };
};

/**
 * Save suggestions (if any) then run the normal decline. A failed save
 * never blocks the decline itself.
 *
 * @param {Object|string} tx
 * @param {Array<string>} picks ISO start times
 * @param {Function} runDecline
 * @returns {Promise}
 */
export const declineWithSuggestions = (tx, picks, runDecline) => {
  const transactionId = txIdOf(tx);
  const starts = (Array.isArray(picks) ? picks : []).slice(0, DECLINE_SUGGESTIONS_MAX);
  const run = () => (typeof runDecline === 'function' ? runDecline() : null);
  if (!transactionId || transactionId.startsWith('preview-') || starts.length === 0) {
    // Nothing to save: decline right away (same as before this feature).
    return Promise.resolve(run());
  }
  return saveDeclineSuggestions({ transactionId, starts })
    .catch(() => null)
    .then(run);
};

/**
 * Sample open times for ?previewBookingPopup= screenshots (no API).
 *
 * @param {Date} [now]
 * @returns {Array<{ start: string, end: string, day: string }>}
 */
export const previewSuggestionOptions = (now = new Date()) => {
  const out = [];
  const base = new Date(now);
  base.setMinutes(0, 0, 0);
  for (let d = 1; d <= 4; d += 1) {
    const day = new Date(base.getTime() + d * 24 * 3600 * 1000);
    day.setHours(10, 0, 0, 0);
    const hours = d % 2 === 0 ? [10, 11, 14, 15, 19] : [9, 12, 13, 17, 18, 20];
    hours.forEach(h => {
      [0, 30].forEach(m => {
        const at = new Date(day);
        at.setHours(h, m, 0, 0);
        out.push({
          start: at.toISOString(),
          end: new Date(at.getTime() + SESSION_MS).toISOString(),
          day: `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(
            at.getDate()
          ).padStart(2, '0')}`,
        });
      });
    });
  }
  return out;
};
